import { readAskScanLocation } from "./askScanLocation";
import { clearThinkingClock } from "./aiChatLoader";
import { parseAssistantPayload } from "./assistantPayload";
import {
  notifyAiTriageRecommendation,
  notifySuggestionLabels,
  writeSuggestionLabelsOnMessage,
} from "./suggestionChips";
import { renderMarkdown } from "./markdown";
import {
  contiguousStreamText,
  createStreamingPlayer,
  renderStreamingFrame,
  tokenizeStreamingText,
} from "./streamingText";
import { resolveRecommendationCard } from "./recommendationCard";
import {
  trackAiTriageBookClicked,
  trackAiTriageHelpEmailClicked,
  withAiTriageAttribution,
} from "./aiTriageTracking";
import { getCsrfToken } from "./getCsrfToken";

const PRIMARY_CTA_BOOK = "book";
const PRIMARY_CTA_CONSULT = "consult";
const RECOMMENDATION_EYEBROW_SCAN = "Recommended scan";
const RECOMMENDATION_EYEBROW_NEXT_STEP = "Recommended next step";
const BOOK_SCAN_LABEL = "Book this scan →";
const BOOK_CONSULTATION_LABEL = "Book a consultation →";
const CARD_HOST_ID = "ai-triage-card-host";
const STREAM_STATUS_ID = "ai-triage-stream-status";
const GUARDRAIL_EMERGENCY = "emergency";
const HELP_EMAIL_PANEL_ID = "ai-triage-panel";
const SEARCH_CREATE_PATH = "/searches";
const DEFAULT_SEARCH_AREA = "London";
const BOOK_METHOD_POST = "post";
const SCAN_TYPE_FIELD = "scan_type";
const SEARCH_INPUTS_FIELD = "search_inputs[]";
const AREA_FIELD = "area";
const AUTHENTICITY_TOKEN_FIELD = "authenticity_token";
const SEARCH_INITIATED_AT_KEY = "search_initiated_at";
const CANNOT_BOOK_MESSAGE =
  "We can't book this scan from the chat. Choose it on the scan page.";
const CANNOT_BOOK_LINK_LABEL = "See this scan";
const BOOK_CONTROL_CLASS =
  "ai-triage-recommendation__book inline-flex w-full items-center justify-center min-h-10 px-4 py-2 rounded-xl text-sm font-semibold leading-tight no-underline bg-pink-primary text-green-primary border border-transparent transition-colors hover:bg-pink-light";

function dismissTypingIndicator() {
  document.getElementById("assistant-typing")?.remove();
}

function announceStreamStatus(text) {
  const status = document.getElementById(STREAM_STATUS_ID);
  if (!status) {
    return;
  }
  status.textContent = "";
  status.textContent = text;
}

function recommendationCatalog() {
  const host = document.getElementById(CARD_HOST_ID);
  const raw = host?.dataset?.recommendationCatalog;
  if (!raw) {
    return { products: {}, aliases: {} };
  }

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.products) {
      return {
        products: parsed.products,
        aliases: parsed.aliases || {},
      };
    }
    return { products: parsed, aliases: {} };
  } catch (_error) {
    return { products: {}, aliases: {} };
  }
}

function serverRecommendationCard(element) {
  const raw = element?.dataset?.recommendation;
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
}

function isEmergencyBubble(element) {
  return element?.dataset?.aiTriageGuardrail === GUARDRAIL_EMERGENCY;
}

function helpEmailFromPanel() {
  return (
    document.getElementById(HELP_EMAIL_PANEL_ID)?.dataset?.aiTriageHelpEmail ||
    ""
  );
}

const EMERGENCY_HELP_SUBJECT = "URGENT – Ask Scan emergency flag (ref %s)";
const EMERGENCY_HELP_BODY =
  "Please add your name and a phone number so we can follow up. Reference: %s";

function chatReferenceFromPanel() {
  return (
    document.getElementById(HELP_EMAIL_PANEL_ID)?.dataset?.aiTriageChatId || ""
  );
}

function mailtoQueryValue(value) {
  return value
    .replace(/%/g, "%25")
    .replace(/ /g, "%20")
    .replace(/&/g, "%26")
    .replace(/\?/g, "%3F")
    .replace(/#/g, "%23");
}

function emergencyMailtoHref(email) {
  const chatId = chatReferenceFromPanel();
  if (!chatId) {
    return `mailto:${email}`;
  }
  const subject = EMERGENCY_HELP_SUBJECT.replace("%s", chatId);
  const body = EMERGENCY_HELP_BODY.replace("%s", chatId);
  return `mailto:${email}?subject=${mailtoQueryValue(
    subject
  )}&body=${mailtoQueryValue(body)}`;
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function linkifyHelpEmail(html) {
  const email = helpEmailFromPanel();
  if (!email) {
    return html;
  }
  const href = emergencyMailtoHref(email);
  const alreadyFilled = `mailto:${email}?`;
  if (html.includes(alreadyFilled)) {
    return html;
  }
  // Marked autolinks the address to a bare mailto before we see the HTML.
  // Leaving that href in place drops the subject and body.
  const bareAnchor = new RegExp(
    `<a href="mailto:${escapeRegExp(email)}"[^>]*>${escapeRegExp(email)}</a>`,
    "g"
  );
  const text = html.replace(bareAnchor, email);
  return text.split(email).join(`<a href="${href}">${email}</a>`);
}

const HELP_EMAIL_CLICK = "click";
let helpEmailClickBound = false;

function isEmergencyHelpLink(target) {
  if (!(target instanceof Element)) {
    return false;
  }
  const link = target.closest("a");
  if (!link) {
    return false;
  }
  const bubble = link.closest(
    `[data-ai-triage-guardrail="${GUARDRAIL_EMERGENCY}"]`
  );
  if (!bubble) {
    return false;
  }
  const href = link.getAttribute("href") || "";
  const email = helpEmailFromPanel();
  return href.startsWith("mailto:") && (!email || href.includes(email));
}

function onHelpEmailClick(event) {
  if (!isEmergencyHelpLink(event.target)) {
    return;
  }
  trackAiTriageHelpEmailClicked();
}

function bindHelpEmailClickTracking() {
  if (helpEmailClickBound) {
    return;
  }
  helpEmailClickBound = true;
  document.addEventListener(HELP_EMAIL_CLICK, onHelpEmailClick);
}

export function registerAiChatMessage(Alpine, onRender) {
  bindHelpEmailClickTracking();
  const notifyRendered = () => {
    if (typeof onRender === "function") {
      onRender();
    }
  };

  Alpine.data("aiChatMessage", (streaming = false) => ({
    streaming,
    observer: null,
    player: null,
    _announcedWriting: false,
    _settled: false,
    _awaitingSettle: false,

    init() {
      if (this.streaming) {
        this.player = createStreamingPlayer({
          onFrame: ({ tokens, count, done }) => {
            const target = this.$refs.content;
            if (!target) {
              return;
            }
            renderStreamingFrame(target, tokens, count, {
              showCaret: !done,
            });
            notifyRendered();
          },
        });
        announceStreamStatus("Scan AI is replying");
        if (this.$refs.streamSource) {
          this.observer = new MutationObserver(() => this.syncFromBuffer());
          this.observer.observe(this.$refs.streamSource, {
            childList: true,
            characterData: true,
            subtree: true,
            attributes: true,
          });
        }
      }

      this.syncFromBuffer();
    },

    destroy() {
      this.observer?.disconnect();
      this.player?.stop();
      if (this.streaming && this._announcedWriting && !this._settled) {
        announceStreamStatus("Scan AI finished replying");
      }
    },

    syncFromBuffer() {
      const raw = contiguousStreamText(this.$refs.raw);
      if (this.streaming && !this._settled && raw.trim().length === 0) {
        this.syncSettle();
        return;
      }

      if (this.streaming && !this._announcedWriting && raw.trim().length > 0) {
        this._announcedWriting = true;
        clearThinkingClock();
        dismissTypingIndicator();
        announceStreamStatus("Scan AI is writing a reply");
      }

      const parsed = this.parseRaw(raw);
      if (this.streaming && !this._settled && this.player) {
        this.player.setTokens(tokenizeStreamingText(parsed.displayText));
        this.syncSettle();
        return;
      }

      this.paintSettled(parsed);
    },

    syncSettle() {
      const settle = this.settleEl();
      if (
        !this.streaming ||
        this._settled ||
        this._awaitingSettle ||
        settle?.dataset?.complete !== "true"
      ) {
        return;
      }

      this._awaitingSettle = true;
      if (settle.dataset.recommendation) {
        this.$el.dataset.recommendation = settle.dataset.recommendation;
      }
      if (!this.player || this.settledSourceText().trim().length === 0) {
        this.settleToMarkdown();
        return;
      }
      this.player.whenCaughtUp(() => this.settleToMarkdown());
    },

    settledSourceText() {
      // The saved message is the in-order accumulator. The raw bubble is
      // arrival order and can still be missing a chunk, so the settled copy
      // wins once it is on the page.
      const settled =
        this.settleEl()?.querySelector("[data-ai-chat-settled-text]")
          ?.textContent ?? "";
      if (settled.trim().length > 0) {
        return settled;
      }
      return contiguousStreamText(this.$refs.raw);
    },

    settleEl() {
      const id = this.$el?.id;
      if (id) {
        const byId = document.getElementById(`${id}_settle`);
        if (byId) {
          return byId;
        }
      }
      return this.$refs.settle || null;
    },

    parseRaw(raw) {
      return parseAssistantPayload(raw, recommendationCatalog());
    },

    settleToMarkdown() {
      if (this._settled) {
        return;
      }
      this._settled = true;
      this.streaming = false;
      this.observer?.disconnect();
      this.player?.stop();
      clearThinkingClock();
      this.$el.removeAttribute("data-ai-chat-streaming");
      dismissTypingIndicator();
      this.paintSettled(this.parseRaw(this.settledSourceText()));
      if (this._announcedWriting) {
        announceStreamStatus("Scan AI finished replying");
      }
    },

    paintSettled({ displayText, suggestions, recommendation }) {
      const target = this.$refs.content;
      if (!target) {
        return;
      }

      if (isEmergencyBubble(this.$el)) {
        this.applyFollowUpSuggestions([]);
        target.innerHTML = linkifyHelpEmail(renderMarkdown(displayText));
        this.renderRecommendationCardElement(null);
        notifyRendered();
        return;
      }

      const catalog = recommendationCatalog();
      const serverCard = serverRecommendationCard(this.$el);
      const card =
        serverCard || resolveRecommendationCard(recommendation, catalog);
      // The card already carries the next step; chips beside it read as filler ("Ready to book").
      this.applyFollowUpSuggestions(card ? [] : suggestions);
      target.innerHTML = renderMarkdown(displayText);

      this.renderRecommendationCardElement(card, {
        showActions: Boolean(serverCard),
      });
      notifyRendered();
    },

    applyFollowUpSuggestions(suggestions) {
      notifySuggestionLabels(
        writeSuggestionLabelsOnMessage(this.$el, suggestions)
      );
    },

    renderRecommendationCardElement(card, { showActions = false } = {}) {
      const container = this.$refs.recommendation;
      if (!container) {
        return;
      }

      container.replaceChildren();

      if (!card) {
        container.hidden = true;
        return;
      }

      container.hidden = false;
      container.appendChild(
        buildRecommendationCardElement(card, { showActions })
      );
      if (showActions) {
        notifyAiTriageRecommendation({
          productId: card.productId,
          bodyPart:
            Array.isArray(card.bodyParts) && card.bodyParts.length
              ? card.bodyParts.join(" + ")
              : card.bodyPart,
          area: card.area,
        });
      }
    },
  }));
}

export function buildRecommendationCardElement(
  card,
  { showActions = false } = {}
) {
  const root = document.createElement("div");
  root.className =
    "ai-triage-recommendation w-full border border-neutral-200 rounded-2xl bg-white px-4 py-4 shadow-sm";
  if (card.productId) {
    root.dataset.productId = card.productId;
  }

  const header = document.createElement("div");
  header.className =
    "ai-triage-recommendation__header flex items-center justify-between gap-3 mb-2.5";

  const eyebrow = document.createElement("p");
  eyebrow.className =
    "ai-triage-recommendation__eyebrow m-0 text-xs font-semibold tracking-wider uppercase text-neutral-400";
  const consultCta = card.primaryCta === PRIMARY_CTA_CONSULT;
  eyebrow.textContent = consultCta
    ? RECOMMENDATION_EYEBROW_NEXT_STEP
    : RECOMMENDATION_EYEBROW_SCAN;
  header.appendChild(eyebrow);

  if (card.fromPriceLabel) {
    const price = document.createElement("span");
    price.className =
      "ai-triage-recommendation__price inline-flex items-center rounded-full bg-green-lightest text-green-light text-xs font-semibold px-2 py-0.5 whitespace-nowrap";
    price.textContent = card.fromPriceLabel;
    header.appendChild(price);
  }

  root.appendChild(header);

  const modality = document.createElement("h3");
  modality.className =
    "ai-triage-recommendation__modality m-0 font-maison-extended text-xl font-bold leading-tight text-neutral-900";
  modality.textContent = card.modality;
  root.appendChild(modality);

  const bodyPart = document.createElement("p");
  bodyPart.className =
    "ai-triage-recommendation__body-part mt-0.5 mb-0 text-sm font-medium text-neutral-700";
  bodyPart.textContent =
    Array.isArray(card.bodyParts) && card.bodyParts.length
      ? card.bodyParts.join(" + ")
      : card.bodyPart;
  root.appendChild(bodyPart);

  if (card.why) {
    const why = document.createElement("p");
    why.className =
      "ai-triage-recommendation__why mt-2.5 mb-0 text-sm leading-snug text-neutral-500";
    why.textContent = card.why;
    root.appendChild(why);
  }

  if (showActions) {
    const actions = document.createElement("div");
    actions.className =
      "ai-triage-recommendation__actions flex flex-wrap gap-2 mt-4";

    const bookCta = consultCta ? PRIMARY_CTA_CONSULT : PRIMARY_CTA_BOOK;
    if (
      card.unresolved &&
      Array.isArray(card.partChoices) &&
      card.partChoices.length > 0
    ) {
      actions.appendChild(buildPartPicker(card, bookCta));
    } else if (card.unresolved) {
      actions.appendChild(buildUnresolvedFallback(card, bookCta));
    } else {
      actions.appendChild(buildBookControl(card, { consultCta, bookCta }));
    }

    root.appendChild(actions);
  }

  return root;
}

function buildBookControl(card, { consultCta, bookCta }) {
  if (card.centresOnly) {
    return buildCentresLink(card, card.bookLabel);
  }

  if (usesSearchCreateForm(card) && !consultCta) {
    return buildSearchBookForm(card, bookCta);
  }

  const book = document.createElement("a");
  book.className = BOOK_CONTROL_CLASS;
  book.href = withAiTriageAttribution(card.bookPath, {
    productId: card.productId,
    cta: bookCta,
  });
  book.textContent = consultCta ? BOOK_CONSULTATION_LABEL : BOOK_SCAN_LABEL;
  book.addEventListener("click", () => {
    trackAiTriageBookClicked({
      productId: card.productId,
      cta: bookCta,
    });
  });
  return book;
}

function usesSearchCreateForm(card) {
  return (
    card.bookMethod === BOOK_METHOD_POST &&
    card.scanType != null &&
    Array.isArray(card.searchInputs) &&
    card.searchInputs.length > 0
  );
}

function buildCentresLink(card, label) {
  const centres = document.createElement("a");
  centres.className =
    "ai-triage-recommendation__centres inline-flex items-center justify-center min-h-10 px-4 py-2 rounded-xl text-sm font-semibold leading-tight no-underline bg-white text-neutral-700 border border-neutral-200 transition-colors hover:border-neutral-300 hover:text-neutral-900";
  centres.href = withAiTriageAttribution(card.centresPath || card.bookPath, {
    productId: card.productId,
    cta: "centres",
  });
  centres.textContent = label;
  centres.addEventListener("click", () => {
    trackAiTriageBookClicked({
      productId: card.productId,
      cta: "centres",
    });
  });
  return centres;
}

function buildPartPicker(card, bookCta) {
  const picker = document.createElement("div");
  picker.className = "ai-triage-recommendation__parts flex flex-wrap gap-2";
  card.partChoices.forEach((choice) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className =
      "ai-triage-recommendation__part inline-flex items-center justify-center min-h-10 px-4 py-2 rounded-xl text-sm font-semibold leading-tight bg-white text-neutral-800 border border-neutral-200";
    button.textContent = choice.label;
    button.addEventListener("click", () => {
      const chosen = {
        ...card,
        unresolved: false,
        searchInputs: [choice.id],
        scanType: card.scanType,
      };
      const form = buildSearchBookForm(chosen, bookCta);
      picker.replaceWith(form);
    });
    picker.appendChild(button);
  });
  return picker;
}

function buildSearchBookForm(card, bookCta) {
  const form = document.createElement("form");
  form.className =
    "ai-triage-recommendation__book-form m-0 flex w-full flex-col gap-3";
  form.action = withAiTriageAttribution(SEARCH_CREATE_PATH, {
    productId: card.productId,
    cta: bookCta,
  });
  form.method = BOOK_METHOD_POST;
  form.acceptCharset = "UTF-8";

  const csrfToken = getCsrfToken();
  if (csrfToken) {
    form.appendChild(hiddenInput(AUTHENTICITY_TOKEN_FIELD, csrfToken));
  }
  form.appendChild(hiddenInput(SCAN_TYPE_FIELD, String(card.scanType)));
  card.searchInputs.forEach((id) => {
    form.appendChild(hiddenInput(SEARCH_INPUTS_FIELD, String(id)));
  });

  form.appendChild(hiddenInput(AREA_FIELD, searchAreaForCard(card)));

  const book = document.createElement("button");
  book.type = "submit";
  book.className = BOOK_CONTROL_CLASS;
  book.textContent = BOOK_SCAN_LABEL;
  form.appendChild(book);

  form.addEventListener("submit", () => {
    window.sessionStorage.setItem(SEARCH_INITIATED_AT_KEY, `${Date.now()}`);
    trackAiTriageBookClicked({
      productId: card.productId,
      cta: bookCta,
    });
  });
  return form;
}

function searchAreaForCard(card) {
  const stored = readAskScanLocation();
  if (stored) {
    return stored;
  }
  const area = `${card?.area || ""}`.trim();
  return area || DEFAULT_SEARCH_AREA;
}

function buildUnresolvedFallback(card, bookCta) {
  const wrap = document.createElement("div");
  wrap.className = "ai-triage-recommendation__unresolved flex flex-col gap-2";
  const message = document.createElement("p");
  message.className = "m-0 text-sm leading-snug text-neutral-600";
  message.textContent = CANNOT_BOOK_MESSAGE;
  const link = document.createElement("a");
  link.className = BOOK_CONTROL_CLASS;
  link.href = withAiTriageAttribution(card.bookPath || card.centresPath, {
    productId: card.productId,
    cta: bookCta,
  });
  link.textContent = CANNOT_BOOK_LINK_LABEL;
  wrap.appendChild(message);
  wrap.appendChild(link);
  return wrap;
}

function hiddenInput(name, value) {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value;
  return input;
}

export { PRIMARY_CTA_BOOK };
